import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { AuthAppService } from 'avn/modules/services/auth-app/auth-app.service';
import { SharedCompsService } from 'avn/modules/services/shared-comps.service';

import { environment } from '../../../environments/environment';

const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;
const HTTP_STATUS_BAD_REQUEST = 400;
const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_FORBIDDEN = 403;
const SNACKBAR_DURATION_SECONDS = 5;

@Injectable()
export class ErrorInterceptor implements ErrorHandler {
  private router: Router | null = null;
  private authService: AuthAppService | null = null;
  private shared: SharedCompsService | null = null;

  public constructor(
    private readonly injector: Injector,
    @Inject(PLATFORM_ID) private readonly platformId: any
  ) {}

  public handleError(error: any): void {
    const normalizedError: any =
      error instanceof HttpErrorResponse ? error : error?.rejection || error?.originalError || error;

    if (normalizedError === 'Not logged in') {
      return;
    }

    const currentRoute = this.getCurrentRoute();
    const url = currentRoute.split('/')[1];
    const status = normalizedError?.status;

    // Ignore generic server errors here; components can still handle the HTTP error locally.
    if (status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
      return;
    }

    const errorMessage = this.extractMessage(normalizedError);
    const rawErrorMessage = this.extractMessage(error);
    const requestUrl = (
      normalizedError?.url ||
      normalizedError?.error?.url ||
      errorMessage ||
      rawErrorMessage ||
      ''
    ).toString();
    const isBlogApiRequest = requestUrl.includes('/blog/wp-json/');
    const isLoginRedirectUrl = currentRoute.startsWith('/login') && currentRoute.includes('redirect=');
    const isExpectedAuthRedirectRejection =
      isLoginRedirectUrl &&
      (errorMessage === 'Not logged in' ||
        rawErrorMessage.includes('This time slot is already reserved by another user') ||
        rawErrorMessage.includes('Uncaught (in promise): Not logged in') ||
        rawErrorMessage.includes('Uncaught (in promise): Error: Not logged in'));

    // Ignore known noisy browser/network failures for blog WP endpoints.
    // These usually surface as status 0 (blocked/CORS/network) and are not actionable app errors.
    if (isBlogApiRequest && status === 0) {
      return;
    }

    // Ignore requests cancelled client-side (e.g. component destroyed, switchMap cancel).
    // These surface as status 0 with no real network failure behind them.
    if (this.isCancelledRequest(normalizedError)) {
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      console.info('[GlobalErrorHandler] Error captured on route:', currentRoute, error);
    }
    if (!['blog'].includes(url) && normalizedError?.error?.message) {
      this.getSharedCompsService()?.showSnackBar(normalizedError?.error?.message, SNACKBAR_DURATION_SECONDS);
      return;
    } else if (
      !['blog'].includes(url) &&
      (status === HTTP_STATUS_UNAUTHORIZED || status === HTTP_STATUS_FORBIDDEN || status === HTTP_STATUS_BAD_REQUEST)
    ) {
      this.getAuthAppService()?.purgeAuth();

      if (url !== 'login' && ['booking', 'business', 'wellbeing'].includes(url)) {
        this.getRouter()?.navigate(['/login'], { queryParams: { redirect: currentRoute } });
      } else {
        this.getRouter()?.navigate(['/']);
      }
      return;
    }

    if (isExpectedAuthRedirectRejection) {
      return;
    }

    if (this.isThirdPartyScriptError(normalizedError, error)) {
      return;
    }

    if (this.isIgnorableSentryErrorShape(normalizedError)) {
      return;
    }

    if (!this.shouldCaptureSentry()) {
      return;
    }

    if (normalizedError instanceof HttpErrorResponse) {
      if (normalizedError.status >= HTTP_STATUS_BAD_REQUEST) {
        return;
      }
      const httpMessage =
        normalizedError?.error?.message ||
        normalizedError?.message ||
        `HTTP ${String(normalizedError.status || 'unknown')} error`;
      const sentryError = new Error(httpMessage);
      sentryError.name = `HttpErrorResponse ${String(normalizedError.status || '')}`.trim();

      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'http');
        scope.setContext('http_error', {
          status: normalizedError.status,
          statusText: normalizedError.statusText,
          url: normalizedError.url || '',
          ok: normalizedError.ok,
          errorMessage: normalizedError?.error?.message || ''
        });
        Sentry.captureException(sentryError);
      });
      return;
    }

    Sentry.captureException(normalizedError?.originalError || normalizedError);
  }

  private shouldCaptureSentry(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const sentryConfig = (environment as any)?.sentry;
    if (!sentryConfig) {
      return true;
    }

    // `enabled` is an opt-out: no environment file currently sets it, so
    // treat it as on by default and only skip capture when explicitly
    // disabled (or when there's no DSN to send to).
    return sentryConfig?.enabled !== false && !!sentryConfig?.dsn;
  }

  private getCurrentRoute(): string {
    const routerUrl = this.getRouter()?.url;

    if (routerUrl) {
      return routerUrl;
    }

    if (isPlatformBrowser(this.platformId)) {
      return `${window.location.pathname}${window.location.search}`;
    }

    return '';
  }

  private getRouter(): Router | null {
    if (!this.router) {
      this.router = this.safeGet(Router);
    }

    return this.router;
  }

  private getAuthAppService(): AuthAppService | null {
    if (!this.authService) {
      this.authService = this.safeGet(AuthAppService);
    }

    return this.authService;
  }

  private getSharedCompsService(): SharedCompsService | null {
    if (!this.shared) {
      this.shared = this.safeGet(SharedCompsService);
    }

    return this.shared;
  }

  private safeGet<T>(token: any): T | null {
    try {
      return this.injector.get<T>(token);
    } catch {
      return null;
    }
  }

  private extractMessage(err: any): string {
    if (!err) {
      return '';
    }
    if (typeof err === 'string') {
      return err;
    }
    return (err?.error?.message || err?.message || err?.toString?.() || '').toString();
  }

  private isCancelledRequest(err: any): boolean {
    if (!(err instanceof HttpErrorResponse) || err.status !== 0) {
      return false;
    }

    const statusText = (err.statusText || '').toString().toLowerCase();
    const message = (err.message || '').toString().toLowerCase();

    return statusText === 'unknown error' || message.includes('abort');
  }

  private isIgnorableSentryErrorShape(err: any): boolean {
    if (!err || typeof err !== 'object' || Array.isArray(err)) {
      return false;
    }

    const expectedKeys = ['error', 'headers', 'message', 'name', 'ok'];
    const keys = Object.keys(err).sort();

    return keys.length === expectedKeys.length && expectedKeys.every((key) => keys.includes(key));
  }

  private isThirdPartyScriptError(normalizedError: any, originalError: any): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const firstPartyHosts = this.getFirstPartyHostnames();
    if (!firstPartyHosts.size) {
      return false;
    }

    const sourceUrls = this.extractSourceUrls(normalizedError, originalError);
    if (!sourceUrls.length) {
      return false;
    }

    const firstSourceHost = this.getHostnameFromUrl(sourceUrls[0]);
    if (firstSourceHost && !firstPartyHosts.has(firstSourceHost)) {
      return true;
    }

    const sourceHosts = sourceUrls.map((url) => this.getHostnameFromUrl(url)).filter((host): host is string => !!host);

    return sourceHosts.length > 0 && sourceHosts.every((host) => !firstPartyHosts.has(host));
  }

  private getFirstPartyHostnames(): Set<string> {
    const hostnames = new Set<string>();
    const currentHostName = window?.location?.hostname;

    if (currentHostName) {
      hostnames.add(currentHostName);
    }

    return hostnames;
  }

  private extractSourceUrls(normalizedError: any, originalError: any): string[] {
    const stackSources = [
      normalizedError?.stack,
      originalError?.stack,
      normalizedError?.rejection?.stack,
      originalError?.rejection?.stack
    ]
      .filter(Boolean)
      .join('\n');

    if (!stackSources) {
      return [];
    }

    const matchedUrls = stackSources.match(/(?:https?:\/\/|file:\/\/|chrome-extension:\/\/)[^\s)]+/g) || [];
    const uniqueUrls: string[] = [];
    const seen = new Set<string>();

    matchedUrls.forEach((rawUrl) => {
      const sanitizedUrl = rawUrl.replace(/[,\];]+$/, '');
      if (!sanitizedUrl || seen.has(sanitizedUrl)) {
        return;
      }
      seen.add(sanitizedUrl);
      uniqueUrls.push(sanitizedUrl);
    });

    return uniqueUrls;
  }

  private getHostnameFromUrl(url: string): string | null {
    try {
      return new URL(url).hostname || null;
    } catch {
      return null;
    }
  }
}
